import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gtagQueue } from '@/lib/gtag';

/**
 * LE TAG GOOGLE ANALYTICS 4 DU SITE — `G-X0T2CKRTQB`, sur toutes les pages.
 *
 * POURQUOI CE FICHIER EXISTE. La propriété GA4 « Aikka » a cessé de recevoir
 * le 23 août 2026 : le conteneur GTM de `index.html` est installé partout,
 * mais son espace PUBLIÉ est vide — zéro tag, zéro déclencheur — et un
 * conteneur vide ne peut être rempli que depuis l'interface Tag Manager,
 * publication comprise. La mesure ne doit pas dépendre d'un clic qui n'est
 * pas venu : le tag GA4 est donc posé directement par le site, comme le
 * sont déjà Intercom, Contentsquare, le tag Google Ads de l'agent et le tag
 * LinkedIn. La frontière de `lib/analytics.ts` tient toujours — ce composant
 * porte l'identifiant, la couche de mesure reste sans identifiant ; et le
 * conteneur GTM reste en place : le jour où il reçoit son tag GA4, il faudra
 * retirer CE composant ou le sien, sinon chaque page vue comptera deux fois.
 *
 * LE CONSENTEMENT, DÉJÀ TRANCHÉ PAR index.html. Le head pose les défauts du
 * Consent Mode (`analytics_storage` etc. `denied`) avant tout script, et la
 * file `dataLayer` est partagée : ce gtag.js relit ces défauts et n'écrit
 * AUCUN cookie sans accord (pings sans cookie). Le jour où le visiteur
 * accepte, `pushConsentUpdate` (`lib/analytics.ts`) pousse AUSSI la vraie
 * commande `gtag('consent', 'update')` — la mesure passe alors en cookies
 * réels, sans rechargement.
 *
 * LES PAGES VUES SONT POSÉES PAR LE CODE, PAS PAR LE FLUX. La configuration
 * part avec `send_page_view: false` : ni l'événement automatique du chargement,
 * ni la mesure améliorée « changements d'historique » (réglage du flux, hors de
 * portée d'ici) ne décident de ce qui compte. C'est ce composant qui émet un
 * `page_view` à chaque changement de route — premier chargement compris, un
 * seul par navigation, avec le titre posé par `<Seo>` et la langue résolue.
 * Le déclencheur GTM historique `virtual_pageview` continue d'être poussé
 * par `AnalyticsProvider` : GA4 direct et GTM ont chacun leur événement, et
 * les deux ne se mélangent pas.
 *
 * En production seulement — sur le patron d'`ENABLED` dans
 * `ContentsquareProvider` : `VITE_GA4_DEV=true` lève la barrière pour
 * vérifier le tag en local.
 */

/** Le flux web « Aikka » de la propriété GA4 522948528. Public par nature. */
const GA4_MEASUREMENT_ID = 'G-X0T2CKRTQB';

/** Production, ou dev avec la barrière levée. */
export function ga4Enabled(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_GA4_DEV === 'true';
}

/**
 * Le tag GA4 de tout le site.
 *
 * À placer au-dessus des routes dans `App.tsx`, sous le routeur : il lit
 * `useLocation` pour émettre un `page_view` à chaque navigation cliente.
 * La configuration est posée AVANT le premier `page_view`, dans le même
 * effet — l'ordre de la file est l'ordre des commandes. Ne rend rien.
 */
export function GoogleAnalyticsTag() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!ga4Enabled()) return;
    const push = gtagQueue();
    if (!push) return;

    if (!document.querySelector('script[data-ga4-tag]')) {
      // L'ordre du snippet officiel : le loader d'abord, puis `js`, puis
      // `config` — posés une seule fois par document, la présence du script
      // en est le témoin (un remontage ne dépile pas la file une seconde fois).
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
      script.dataset.ga4Tag = GA4_MEASUREMENT_ID;
      document.head.appendChild(script);
      push('js', new Date());
      // `send_page_view: false` : voir l'en-tête — les pages vues sont émises
      // par le code, une seule fois chacune, indépendamment des réglages du
      // flux.
      push('config', GA4_MEASUREMENT_ID, { send_page_view: false });
    }

    const path = `${location.pathname}${location.search}${location.hash}`;
    // Une même clé deux fois de suite n'est pas une page vue : React peut
    // réexécuter l'effet (StrictMode en développement, remontage d'un parent)
    // sans que le visiteur ait navigué.
    if (lastPath.current === path) return;
    lastPath.current = path;

    // Un cran après la peinture : `<Seo>` pose le titre dans son propre effet,
    // et une page vue qui porte le titre de la page PRÉCÉDENTE est plus
    // trompeuse qu'une page vue absente — le même cran que
    // `AnalyticsProvider`.
    const frame = requestAnimationFrame(() => {
      const eventPush = gtagQueue();
      if (!eventPush) return;
      eventPush('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
        page_language: document.documentElement.lang,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [location]);

  return null;
}
